import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/kit";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import {
  Check, Circle, ShieldCheck, FileText, Sparkles, Loader2,
  BookMarked, ChevronLeft, Building2, Layers, Search, X, Lock, Send,
} from "lucide-react";
import { useProgressSteps } from "../hooks/useProgressSteps";
import { ProgressSteps } from "../components/ui/ProgressSteps";
import UpgradeModal from "../components/UpgradeModal";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../lib/auth";

const START_INTERVIEW_STEPS = [
  { label: "Parsing resume", duration: 3500 },
  { label: "Detecting skills", duration: 4000 },
  { label: "Matching questions", duration: 5000 },
  { label: "Preparing interview", duration: 0 },
];

const ROUND_LABELS = { hr: "HR", technical: "Technical", behavioral: "Behavioral" };
const ROUND_DESCRIPTIONS = {
  hr: "Culture fit, soft skills, motivation, and background",
  technical: "Role-specific technical depth and problem-solving",
  behavioral: "Situation-based questions to assess experience patterns",
};
const ROUND_COLORS = { hr: "bg-[#1D4ED8]", technical: "bg-[#0F172A]", behavioral: "bg-[#7C3AED]" };

export default function NewInterview() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Drill-down state
  const [isDrilldown, setIsDrilldown] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [families, setFamilies] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [drilldownRoles, setDrilldownRoles] = useState([]);
  const [drilldownRolesLoading, setDrilldownRolesLoading] = useState(false);

  // Flat fallback state
  const [flatRoles, setFlatRoles] = useState([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [allRolesFlat, setAllRolesFlat] = useState([]);

  // Shared state
  const [rolesLoading, setRolesLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRounds, setSelectedRounds] = useState([]);
  const [seniority, setSeniority] = useState("Mid-level");
  const [roundCounts, setRoundCounts] = useState({});
  const [rbResume, setRbResume] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const startProgress = useProgressSteps(START_INTERVIEW_STEPS);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [creatingRole, setCreatingRole] = useState(false);
  const [quota, setQuota] = useState(null);          // {used, cap, remaining, is_premium}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [pickerOptions, setPickerOptions] = useState([]); // [{soc_code, domain_label, onet_title}]
  const [chosen, setChosen] = useState(null);             // {soc_code, name}
  const [requestedTitle, setRequestedTitle] = useState(null); // title of a submitted request
  const roleSectionRef = useRef(null);                    // scroll target for a just-created role
  const scrollAfterSelectRef = useRef(false);

  // Resume picker
  const [savedResumes, setSavedResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [selectedResumeVariant, setSelectedResumeVariant] = useState(null);
  const [selectedResumeLabel, setSelectedResumeLabel] = useState("");
  const [loadingResume, setLoadingResume] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/roles/industries").catch(() => ({ data: [] })),
      api.get("/resume/saved").catch(() => ({ data: { resumes: [] } })),
      api.get("/roles/all").catch(() => ({ data: [] })),
    ]).then(([indRes, resumesRes, allRes]) => {
      const inds = indRes.data || [];
      const resumes = resumesRes.data.resumes || [];
      setSavedResumes(resumes);
      setAllRolesFlat(allRes.data || []);

      // Auto-restore last selected resume
      const lastId = localStorage.getItem("mock_last_resume_id");
      const lastVariant = localStorage.getItem("mock_last_resume_variant") || "original";
      if (lastId && resumes.length > 0) {
        const match = resumes.find(r => r.resume_id === lastId);
        if (match) {
          const label = lastVariant === "optimized"
            ? (match.optimized_name || match.name) + " (ATS)"
            : match.name;
          setSelectedResumeId(lastId);
          setSelectedResumeVariant(lastVariant);
          setSelectedResumeLabel(label);
          setLoadingResume(true);
          api.get(`/resume/saved/${lastId}/download`, {
            params: { variant: lastVariant, fmt: "txt_raw" },
            responseType: "text",
          }).then(res => setRbResume(res.data))
            .catch(() => { setSelectedResumeId(null); setSelectedResumeVariant(null); setSelectedResumeLabel(""); })
            .finally(() => setLoadingResume(false));
        }
      }

      if (inds.length > 0) {
        setIndustries(inds);
        setIsDrilldown(true);
        setRolesLoading(false);
      } else {
        // No industries configured — fall back to flat role list
        setIsDrilldown(false);
        api.get("/roles")
          .then(({ data }) => setFlatRoles(data))
          .catch(() => {})
          .finally(() => setRolesLoading(false));
      }
    }).catch(() => setRolesLoading(false));
  }, []);

  const fetchQuota = () =>
    api.get("/roles/auto-role-quota").then(({ data }) => setQuota(data)).catch(() => {});
  useEffect(() => { fetchQuota(); }, []);

  // After a just-created role is auto-selected, scroll its setup panel into view so it's obvious.
  useEffect(() => {
    if (selectedRole && scrollAfterSelectRef.current && roleSectionRef.current) {
      roleSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      scrollAfterSelectRef.current = false;
    }
  }, [selectedRole]);

  const selectIndustry = async (industry) => {
    setSelectedIndustry(industry);
    setSelectedFamily(null);
    setDrilldownRoles([]);
    setSelectedRole(null);
    setSelectedRounds([]);
    setFamiliesLoading(true);
    track("interview_setup_industry_selected", { industry_name: industry.name });
    try {
      const { data } = await api.get(`/roles/industries/${industry.industry_id}/families`);
      setFamilies(data);
    } catch {
      toast.error("Could not load job families");
    } finally {
      setFamiliesLoading(false);
    }
  };

  const selectFamily = async (family) => {
    setSelectedFamily(family);
    setSelectedRole(null);
    setSelectedRounds([]);
    setDrilldownRolesLoading(true);
    try {
      const { data } = await api.get(`/roles/families/${family.family_id}/roles`);
      setDrilldownRoles(data);
    } catch {
      toast.error("Could not load roles");
    } finally {
      setDrilldownRolesLoading(false);
    }
  };

  const goBackToIndustries = () => {
    setSelectedIndustry(null);
    setSelectedFamily(null);
    setFamilies([]);
    setDrilldownRoles([]);
    setSelectedRole(null);
    setSelectedRounds([]);
  };

  const goBackToFamilies = () => {
    setSelectedFamily(null);
    setDrilldownRoles([]);
    setSelectedRole(null);
    setSelectedRounds([]);
  };

  const maxForRound = (rnd) => Math.min(5, selectedRole?.[`${rnd}_question_count`] || 0);

  const toggleRound = (rnd) => {
    const adding = !selectedRounds.includes(rnd);
    if (adding) {
      const max = maxForRound(rnd);
      setRoundCounts((c) => ({ ...c, [rnd]: Math.min(c[rnd] || 1, max) }));
      setSelectedRounds((prev) => [...prev, rnd]);
    } else {
      setSelectedRounds((prev) => prev.filter((r) => r !== rnd));
    }
  };

  const setRoundCount = (rnd, n) => setRoundCounts((c) => ({ ...c, [rnd]: n }));

  const startInterview = async () => {
    if (!selectedRole) { toast.error("Select a role"); return; }
    if (selectedRounds.length === 0) { toast.error("Select at least one round"); return; }
    if (!rbResume.trim()) { toast.error("Please select a resume to personalise your questions"); return; }
    setSubmitting(true);
    startProgress.start();
    const questionCounts = Object.fromEntries(selectedRounds.map((r) => [r, roundCounts[r] || 1]));
    track("interview_setup_started", {
      role_id: selectedRole.role_id,
      role_name: selectedRole.name,
      industry_name: selectedRole.industry_name || null,
      family_name: selectedRole.family_name || null,
      rounds: selectedRounds,
      question_counts: questionCounts,
      used_search: isSearchActive,
    });
    try {
      const { data } = await api.post("/interviews", {
        source: "role_based",
        role_id: selectedRole.role_id,
        rounds: selectedRounds,
        question_counts: questionCounts,
        resume_text: rbResume.trim(),
        seniority,
      });
      toast.success("Interview prepared — questions matched to your skills");
      navigate(`/auto-interview/${data.interview_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.plan_limit) {
        setUpgradeReason(detail.limit_type);
        setUpgradeOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not create interview");
      }
    } finally { setSubmitting(false); startProgress.finish(); }
  };

  // Select a resolved role. Prefer the role object returned inline by the create endpoint
  // (immune to list filtering/timing); fall back to a /roles/all lookup for existing roles.
  const selectResolvedRole = async (roleId, name, fresh, roleObj) => {
    let role = roleObj;
    if (role) {
      setAllRolesFlat((prev) => (prev.some((r) => r.role_id === roleId) ? prev : [role, ...prev]));
    } else {
      const all = await api.get("/roles/all").then((r) => r.data).catch(() => allRolesFlat);
      setAllRolesFlat(all || []);
      role = (all || []).find((r) => r.role_id === roleId);
    }
    if (role) {
      // Reflect the full Industry → Family → Role path in the drill-down when categorized.
      if (isDrilldown && role.industry_id && role.family_id && role.industry_name && role.family_name) {
        setSelectedIndustry({ industry_id: role.industry_id, name: role.industry_name });
        setFamilies([{ family_id: role.family_id, name: role.family_name }]);
        setSelectedFamily({ family_id: role.family_id, name: role.family_name });
        setDrilldownRoles([role]);
      }
      scrollAfterSelectRef.current = true;   // scroll the setup panel into view after it renders
      setSelectedRole(role);
      setSelectedRounds([]);
      setSearchQuery("");
      toast.success(fresh ? `Created "${name}" — pick your rounds below` : `Found "${name}"`);
    } else {
      toast.success(`"${name}" is ready — search again to select it`);
    }
  };

  // Entry point — resolve first (cheap), then disambiguate / confirm / request as needed.
  const startCreateFlow = async () => {
    const title = searchQuery.trim();
    if (!title) return;
    setPreviewing(true);
    track("interview_setup_resolve_or_create", { title, premium: isPremium });
    try {
      const { data } = await api.post("/roles/resolve-preview", { title, resume_text: rbResume.trim() || null });
      if (data.status === "ready") {
        await selectResolvedRole(data.role_id, data.name, false);
      } else if (data.status === "choose") {
        setPickerOptions(data.options || []);
        setChosen(null);
        setConfirmOpen(true);                       // dialog opens in picker mode
      } else if (data.status === "auto") {
        if (canCreateInstant) {
          setPickerOptions([]);
          setChosen({ soc_code: data.soc_code, name: data.name });
          setConfirmOpen(true);                     // dialog opens in confirm mode
        } else {
          resolveOrCreate({ soc_code: data.soc_code, name: data.name });   // free → request
        }
      } else {
        resolveOrCreate({ soc_code: null, name: title });                  // low → request
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check that role — please try again");
    } finally { setPreviewing(false); }
  };

  // Candidate picked a field/domain from the disambiguation picker.
  const choosePickerOption = (opt) => {
    if (canCreateInstant) {
      setChosen({ soc_code: opt.soc_code, name: opt.onet_title });
      setPickerOptions([]);                         // switch dialog to confirm mode
    } else {
      setConfirmOpen(false);
      setPickerOptions([]);
      resolveOrCreate({ soc_code: opt.soc_code, name: opt.onet_title });   // free → request the chosen variant
    }
  };

  // The actual create/request call. `override` carries an already-disambiguated soc/name.
  const resolveOrCreate = async (override) => {
    const title = searchQuery.trim();
    if (!title && !override?.soc_code) return;
    setCreatingRole(true);
    try {
      const { data } = await api.post("/roles/resolve-or-create", {
        title,
        resume_text: rbResume.trim() || null,
        soc_code: override?.soc_code || undefined,
        name: override?.name || undefined,
      });
      if (data.status === "ready") {
        await selectResolvedRole(data.role_id, data.name, data.fresh, data.role);
      } else {
        // request_submitted | pending_review | cap_reached — show a persistent confirmation
        const displayTitle = override?.name || title;
        setRequestedTitle(displayTitle);
        setSearchQuery("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not process that role — please try again");
    } finally { setCreatingRole(false); fetchQuota(); }
  };

  const handleSelectResume = async (resumeId, variant, label) => {
    setLoadingResume(true);
    setSelectedResumeId(resumeId);
    setSelectedResumeVariant(variant);
    setSelectedResumeLabel(label);
    try {
      const res = await api.get(`/resume/saved/${resumeId}/download`, {
        params: { variant, fmt: "txt_raw" },
        responseType: "text",
      });
      setRbResume(res.data);
      localStorage.setItem("mock_last_resume_id", resumeId);
      localStorage.setItem("mock_last_resume_variant", variant);
    } catch {
      toast.error("Could not load resume text");
      setSelectedResumeId(null);
      setSelectedResumeLabel("");
    } finally {
      setLoadingResume(false);
    }
  };

  const roleList = isDrilldown ? drilldownRoles : flatRoles;
  const totalQuestions = selectedRounds.reduce((sum, r) => sum + (roundCounts[r] || 1), 0);
  const canStart = selectedRole && selectedRounds.length > 0 && rbResume.trim().length > 0;

  // Industry › Family breadcrumb for the selected role (from the role's own names or the drill-down state).
  const selectedHierPath = selectedRole
    ? [selectedRole.industry_name || selectedIndustry?.name, selectedRole.family_name || selectedFamily?.name]
        .filter(Boolean).join(" › ")
    : "";

  // Search derived values
  const isPremium = user?.plan === "premium";
  const isSearchActive = searchQuery.trim().length > 0;
  const searchResults = isSearchActive
    ? allRolesFlat.filter((r) => {
        const q = searchQuery.toLowerCase();
        return (
          r.name.toLowerCase().includes(q) ||
          (r.industry_name || "").toLowerCase().includes(q) ||
          (r.family_name || "").toLowerCase().includes(q) ||
          (r.description || "").toLowerCase().includes(q)
        );
      })
    : [];

  // Premium with monthly creations left → instant create (with confirm); else request path.
  const canCreateInstant = quota ? (quota.is_premium && quota.remaining > 0) : isPremium;
  const busy = previewing || creatingRole;

  // Create/request action buttons — shared by the no-results panel and the below-results row.
  const affordanceButtons = (size = "md") => {
    const q = searchQuery.trim();
    const pad = size === "sm" ? "px-3.5 py-2 text-xs" : "px-5 py-2.5 text-sm";
    if (canCreateInstant) {
      return (
        <button type="button" onClick={startCreateFlow} disabled={busy}
          className={`inline-flex items-center gap-2 rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${pad}`}>
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {previewing ? "Checking…" : "Building…"}</> : <><Sparkles className="w-4 h-4" /> Create "{q}"</>}
        </button>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 flex-wrap">
        <button type="button" onClick={startCreateFlow} disabled={busy}
          className={`inline-flex items-center gap-2 rounded-xl border border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#1D4ED8]/5 font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${pad}`}>
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {previewing ? "Checking…" : "Sending…"}</> : <><Send className="w-4 h-4" /> Request "{q}"</>}
        </button>
        {!isPremium && (
          <button type="button" onClick={() => { setUpgradeReason("premium_role"); setUpgradeOpen(true); }}
            className={`inline-flex items-center gap-2 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold transition-all ${pad}`}>
            <Sparkles className="w-4 h-4" /> Upgrade
          </button>
        )}
      </div>
    );
  };

  // Step counter offsets: flatten when search bypasses the drill-down
  const roundStepNum = (isDrilldown && !isSearchActive) ? 4 : 2;
  const resumeStepNum = (isDrilldown && !isSearchActive) ? 5 : 3;

  // ─── Role selection card (shared between all list contexts) ──────────────
  const RoleCard = ({ r, breadcrumb }) => {
    const isLocked = r.premium_only && user?.plan !== "premium";
    return (
      <button key={r.role_id} type="button"
        onClick={() => {
          if (isLocked) {
            setUpgradeReason("premium_role");
            setUpgradeOpen(true);
            return;
          }
          setSelectedRole(r); setSelectedRounds([]); setRoundCounts({});
          track("interview_setup_role_selected", {
            role_id: r.role_id,
            role_name: r.name,
            industry_name: r.industry_name || null,
            family_name: r.family_name || null,
            via_search: isSearchActive,
          });
        }}
        className={`text-left border p-4 transition-all rounded-xl ${
          isLocked
            ? "border-border bg-secondary/20 opacity-70 cursor-pointer"
            : selectedRole?.role_id === r.role_id
              ? "border-[#1D4ED8] bg-[#1D4ED8]/5"
              : "border-border bg-white hover:bg-secondary/60"
        }`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="font-semibold text-sm truncate">{r.name}</div>
              {isLocked && (
                <span className="flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] border border-[#7C3AED]/20 flex-shrink-0">
                  <Lock className="w-2.5 h-2.5" /> Premium
                </span>
              )}
            </div>
            {breadcrumb && (
              <div className="font-mono text-[10px] text-[#1D4ED8]/70 mt-0.5 truncate">{breadcrumb}</div>
            )}
            {r.description && <div className="text-xs text-muted-foreground truncate mt-0.5">{r.description}</div>}
            <div className="font-mono text-[10px] text-muted-foreground mt-2">
              {["hr", "technical", "behavioral"].filter((rnd) => (r[`${rnd}_question_count`] || 0) > 0).length} / 3 rounds ready
            </div>
          </div>
          <div className={`w-5 h-5 border flex-shrink-0 flex items-center justify-center ${
            isLocked ? "border-border bg-secondary/30" : selectedRole?.role_id === r.role_id ? "bg-[#1D4ED8] border-[#1D4ED8]" : "border-border"
          }`}>
            {isLocked
              ? <Lock className="w-2.5 h-2.5 text-muted-foreground" />
              : selectedRole?.role_id === r.role_id && <Check className="w-3 h-3 text-white" />}
          </div>
        </div>
      </button>
    );
  };

  return (
    <>
    <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />

    {/* Two-mode dialog: field picker (ambiguous title) → confirm + cost */}
    <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setPickerOptions([]); setChosen(null); } }}>
      <DialogContent className="rounded-xl">
        {pickerOptions.length > 0 ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-bold">Which "{searchQuery.trim()}"?</DialogTitle>
              <DialogDescription>
                This title exists across a few fields. Pick the one that matches you so we tailor the right questions.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {pickerOptions.map((opt) => (
                <button key={opt.soc_code} type="button" onClick={() => choosePickerOption(opt)} disabled={busy}
                  className="w-full text-left rounded-xl border border-border hover:border-[#1D4ED8] hover:bg-[#1D4ED8]/5 px-4 py-3 transition-all disabled:opacity-50">
                  <div className="text-sm font-semibold text-[#1D4ED8]">{opt.domain_label}</div>
                  <div className="text-xs text-muted-foreground">{opt.onet_title}</div>
                </button>
              ))}
            </div>
            <DialogFooter>
              <button type="button" onClick={() => { setConfirmOpen(false); setPickerOptions([]); }}
                className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary transition-all">
                Cancel
              </button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-bold">Create "{chosen?.name || searchQuery.trim()}"?</DialogTitle>
              <DialogDescription>
                We'll build a tailored interview for this role in a few seconds.
                {quota && quota.is_premium && (
                  <> This uses 1 of your {quota.cap} monthly role creations — <strong>{quota.remaining} left</strong>.</>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button type="button" onClick={() => { setConfirmOpen(false); setChosen(null); }}
                className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary transition-all">
                Cancel
              </button>
              <button type="button" onClick={() => { setConfirmOpen(false); resolveOrCreate(chosen); }}
                className="inline-flex items-center gap-2 rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF] text-white px-4 py-2 text-sm font-semibold transition-all">
                <Sparkles className="w-4 h-4" /> Create role
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8] mb-3">Module 1 · Mock Interview</div>
      <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mb-8">Prepare your session.</h1>

      {/* ── SEARCH BAR ────────────────────────────────────────────────────────── */}
      {!rolesLoading && (
        <div className="mb-8">
          <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">
            {isSearchActive ? "Search results" : "Quick search"}
          </Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                const val = e.target.value;
                if (!searchQuery && val.trim()) track("interview_setup_search_used");
                setSearchQuery(val);
                if (selectedRole && val.trim()) setSelectedRole(null);
                if (requestedTitle && val.trim()) setRequestedTitle(null);
              }}
              placeholder="Search by role name, industry or job family…"
              className="w-full border border-input bg-background pl-9 pr-9 py-3 text-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30 focus:border-[#1D4ED8]"
            />
            {isSearchActive && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Persistent, plan-aware hint — visible even before searching, so browsers discover it too */}
          <p className="mt-2 text-[11px] text-muted-foreground">
            {isPremium
              ? <>Don't see your role? Type its title above and <span className="text-[#1D4ED8] font-semibold">create a tailored interview in seconds</span>.</>
              : <>Don't see your role? Type its title above to <span className="text-[#1D4ED8] font-semibold">request it</span> — or upgrade to create it instantly.</>}
          </p>

          {/* Search results */}
          {isSearchActive && (
            <div className="mt-3">
              {searchResults.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-8 text-center">
                  <p className="text-sm text-muted-foreground mb-1">
                    No roles match <strong>"{searchQuery.trim()}"</strong> in our catalog yet.
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {canCreateInstant
                      ? "We'll build a tailored interview for this role in a few seconds."
                      : isPremium
                        ? "You've used all your monthly role creations — we'll queue this one for our team."
                        : "Request it and our team will add it — or upgrade to create it instantly."}
                  </p>
                  <div className="flex justify-center">{affordanceButtons("md")}</div>
                  <p className="text-[11px] text-muted-foreground mt-4">…or browse by industry below.</p>
                </div>
              ) : (
                <>
                  <div className="font-mono text-[10px] text-muted-foreground mb-2">
                    {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {searchResults.map((r) => {
                      const breadcrumb = [r.industry_name, r.family_name].filter(Boolean).join(" › ");
                      return <RoleCard key={r.role_id} r={r} breadcrumb={breadcrumb || null} />;
                    })}
                  </div>
                  {/* Below-results affordance — surfaced even when partial matches exist */}
                  <div className="mt-4 rounded-xl border border-dashed border-border px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-muted-foreground">
                      Don't see your exact role?{" "}
                      {canCreateInstant
                        ? `Create "${searchQuery.trim()}" instead.`
                        : isPremium
                          ? "Monthly creation limit reached — request it."
                          : "Request it, or upgrade to create instantly."}
                    </span>
                    {affordanceButtons("sm")}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── REQUEST SUBMITTED CONFIRMATION ───────────────────────────────────── */}
      {requestedTitle && !isSearchActive && (
        <div className="mb-8 rounded-xl border border-[#10B981]/40 bg-[#10B981]/5 p-6">
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-full bg-[#10B981]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ShieldCheck className="w-5 h-5 text-[#10B981]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-[#0F172A]">Request submitted for "{requestedTitle}"</div>
              <div className="text-xs text-muted-foreground mt-1">
                Our team has been notified and will add this role soon. You'll receive an email when it's ready to practice.
              </div>
              <button
                type="button"
                onClick={() => setRequestedTitle(null)}
                className="mt-3 font-mono text-[11px] text-[#1D4ED8] hover:underline"
              >
                Request another role →
              </button>
            </div>
          </div>
        </div>
      )}

      {rolesLoading ? (
        <div className="font-mono text-sm text-muted-foreground">Loading…</div>
      ) : isSearchActive ? null : requestedTitle ? null : isDrilldown ? (
        <>
          {/* ── STEP 1: Industry ─────────────────────────────────────────────── */}
          <div className="mb-8">
            <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">
              1. Select industry
            </Label>
            {industries.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
                No industries available. Ask your administrator to configure the role hierarchy.
              </div>
            ) : selectedIndustry ? (
              /* Collapsed — show selected chip + back link */
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 rounded-xl border border-[#1D4ED8] bg-[#1D4ED8]/5 px-4 py-2">
                  <Building2 className="w-4 h-4 text-[#1D4ED8]" />
                  <span className="font-semibold text-sm">{selectedIndustry.name}</span>
                  <Check className="w-4 h-4 text-[#1D4ED8]" />
                </div>
                <button type="button" onClick={goBackToIndustries}
                  className="font-mono text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                  <ChevronLeft className="w-3 h-3" /> Change
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {industries.map((ind) => (
                  <button key={ind.industry_id} type="button"
                    onClick={() => selectIndustry(ind)}
                    className="text-left border border-border bg-white hover:bg-secondary/60 hover:border-[#1D4ED8]/40 p-5 transition-all rounded-xl group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-muted-foreground group-hover:text-[#1D4ED8] transition-colors" />
                          <div className="font-semibold text-sm">{ind.name}</div>
                        </div>
                        {ind.description && <div className="text-xs text-muted-foreground truncate">{ind.description}</div>}
                        <div className="font-mono text-[10px] text-muted-foreground mt-2">
                          {ind.role_count} role{ind.role_count !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-180 flex-shrink-0 mt-0.5" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── STEP 2: Job Family ───────────────────────────────────────────── */}
          {selectedIndustry && (
            <div className="mb-8">
              <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">
                2. Select job family
              </Label>
              {familiesLoading ? (
                <div className="font-mono text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading job families…
                </div>
              ) : families.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
                  No job families in this industry yet.
                </div>
              ) : selectedFamily ? (
                /* Collapsed — show selected chip + back link */
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 rounded-xl border border-[#1D4ED8] bg-[#1D4ED8]/5 px-4 py-2">
                    <Layers className="w-4 h-4 text-[#1D4ED8]" />
                    <span className="font-semibold text-sm">{selectedFamily.name}</span>
                    <Check className="w-4 h-4 text-[#1D4ED8]" />
                  </div>
                  <button type="button" onClick={goBackToFamilies}
                    className="font-mono text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                    <ChevronLeft className="w-3 h-3" /> Change
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {families.map((fam) => (
                    <button key={fam.family_id} type="button"
                      onClick={() => selectFamily(fam)}
                      className="text-left border border-border bg-white hover:bg-secondary/60 hover:border-[#1D4ED8]/40 p-5 transition-all rounded-xl group">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Layers className="w-4 h-4 text-muted-foreground group-hover:text-[#1D4ED8] transition-colors" />
                            <div className="font-semibold text-sm">{fam.name}</div>
                          </div>
                          {fam.description && <div className="text-xs text-muted-foreground truncate">{fam.description}</div>}
                          <div className="font-mono text-[10px] text-muted-foreground mt-2">
                            {fam.role_count} role{fam.role_count !== 1 ? "s" : ""}
                          </div>
                        </div>
                        <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-180 flex-shrink-0 mt-0.5" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Role ─────────────────────────────────────────────────── */}
          {selectedFamily && (
            <div className="mb-8">
              <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">
                3. Select role
              </Label>
              {drilldownRolesLoading ? (
                <div className="font-mono text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading roles…
                </div>
              ) : drilldownRoles.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
                  No roles with approved questions in this job family yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {drilldownRoles.map((r) => <RoleCard key={r.role_id} r={r} />)}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* ── FLAT MODE (no industries configured) ─────────────────────────── */
        <div className="mb-8">
          <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">1. Available roles</Label>
          {flatRoles.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
              No interview roles available yet. Ask your administrator to set up a role.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {flatRoles.map((r) => <RoleCard key={r.role_id} r={r} />)}
            </div>
          )}
        </div>
      )}

      {/* ── STEP: Experience level (tunes question difficulty) ──────────────── */}
      {selectedRole && (
        <div ref={roleSectionRef} className="mb-8 scroll-mt-6">
          {/* Selected-role confirmation with full Industry › Family › Role path */}
          <div className="flex items-center gap-2.5 rounded-xl border border-[#1D4ED8] bg-[#1D4ED8]/5 px-4 py-2.5 mb-4">
            <Check className="w-4 h-4 text-[#1D4ED8] flex-shrink-0" />
            <div className="min-w-0">
              {selectedHierPath && (
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#1D4ED8]/70 truncate">{selectedHierPath}</div>
              )}
              <div className="font-semibold text-sm text-[#0F172A] truncate">{selectedRole.name}</div>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#1D4ED8]/70 ml-auto flex-shrink-0">Selected</span>
          </div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">
            Experience level <span className="font-normal text-muted-foreground normal-case">(tunes question difficulty)</span>
          </Label>
          <div className="flex flex-wrap gap-2">
            {[["Junior", "Junior / Entry"], ["Mid-level", "Mid-level"], ["Senior", "Senior / Lead"]].map(([val, label]) => (
              <button key={val} type="button" onClick={() => setSeniority(val)}
                className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-all ${
                  seniority === val ? "bg-[#1D4ED8] border-[#1D4ED8] text-white" : "border-border text-muted-foreground hover:border-[#1D4ED8]/40"
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── STEP: Rounds (multi-select with per-round count) ─────────────────── */}
      {selectedRole && (
        <div className="mb-8">
          <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">
            {roundStepNum}. Select rounds <span className="font-normal text-muted-foreground normal-case">(pick one or more — set questions per round)</span>
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["hr", "technical", "behavioral"].map((rnd) => {
              const bankCount = selectedRole[`${rnd}_question_count`] || 0;
              const ready = bankCount > 0;
              const isSelected = selectedRounds.includes(rnd);
              const max = maxForRound(rnd);
              const current = roundCounts[rnd] || 1;
              return (
                <div key={rnd} className={`border transition-all rounded-xl ${!ready ? "opacity-40 border-border" : isSelected ? "border-[#1D4ED8]" : "border-border"}`}>
                  {/* Clickable top — toggles round on/off */}
                  <button
                    type="button"
                    disabled={!ready}
                    onClick={() => {
                      toggleRound(rnd);
                      track("interview_setup_round_selected", {
                        role_id: selectedRole.role_id,
                        role_name: selectedRole.name,
                        round: rnd,
                        action: isSelected ? "deselect" : "select",
                      });
                    }}
                    className={`w-full text-left p-4 transition-colors ${!ready ? "cursor-not-allowed" : isSelected ? "bg-[#1D4ED8]/5 hover:bg-[#1D4ED8]/10" : "bg-white hover:bg-secondary/60"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className={`inline-block px-2 py-0.5 text-[10px] font-mono font-bold uppercase mb-2 ${ROUND_COLORS[rnd]} text-white`}>
                          {ROUND_LABELS[rnd]}
                        </div>
                        <div className="font-semibold text-sm">{ROUND_LABELS[rnd]} Round</div>
                        <div className="text-xs text-muted-foreground mt-1">{ROUND_DESCRIPTIONS[rnd]}</div>
                        {!ready && <div className="font-mono text-[10px] text-[#F59E0B] mt-2">No questions in bank yet</div>}
                      </div>
                      <div className={`w-5 h-5 border-2 flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors ${isSelected ? "bg-[#1D4ED8] border-[#1D4ED8]" : "border-border bg-white"}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  </button>
                  {/* Per-round count picker — only when selected */}
                  {isSelected && (
                    <div className="border-t border-[#1D4ED8]/20 px-4 pt-2.5 pb-3 bg-[#1D4ED8]/5">
                      <div className="font-mono text-[10px] text-[#1D4ED8] tracking-[0.2em] mb-2">QUESTIONS</div>
                      <div className="flex gap-1.5">
                        {[1, 2, 3, 4, 5].filter((n) => n <= max).map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setRoundCount(rnd, n); }}
                            className={`w-8 h-8 rounded-lg border font-mono font-bold text-sm transition-all ${current === n ? "border-[#1D4ED8] bg-[#1D4ED8] text-white" : "border-border bg-white hover:bg-secondary"}`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {selectedRounds.length > 0 && (
            <p className="font-mono text-[10px] text-muted-foreground mt-3">
              {selectedRounds.map((r) => `${ROUND_LABELS[r]}: ${roundCounts[r] || 1}`).join(" + ")}
              {" = "}
              <span className="text-foreground font-bold">{totalQuestions} question{totalQuestions !== 1 ? "s" : ""} total</span>
            </p>
          )}
        </div>
      )}

      {/* ── STEP: Resume ─────────────────────────────────────────────────────── */}
      {selectedRole && selectedRounds.length > 0 && (
        <div className="mb-8">
          <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">
            {resumeStepNum}. Your résumé <span className="text-[#E11D48] font-bold">*</span>
          </Label>
          <div className="flex items-start gap-3 rounded-xl border border-[#1D4ED8]/20 bg-[#1D4ED8]/5 px-4 py-3 mb-3">
            <FileText className="w-4 h-4 text-[#1D4ED8] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-[#1D4ED8]">AI skill matching is active.</span>{" "}
              Your resume is analysed to detect your tool stack. Questions are selected to match only the tools and frameworks you have experience with.
            </p>
          </div>

          {selectedResumeId && rbResume && (
            <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-[#1D4ED8]">
              <BookMarked className="w-3 h-3" />
              {selectedResumeLabel}
              <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-[#1D4ED8] text-white">LOADED</span>
              <button
                onClick={() => {
                  setRbResume(""); setSelectedResumeId(null); setSelectedResumeVariant(null); setSelectedResumeLabel("");
                  localStorage.removeItem("mock_last_resume_id");
                  localStorage.removeItem("mock_last_resume_variant");
                }}
                className="text-muted-foreground hover:text-[#E11D48] ml-1 text-[10px]">× Clear</button>
            </div>
          )}

          {savedResumes.length === 0 ? (
            <div className="border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">No resumes saved yet.</p>
              <Link to="/resumes" className="text-xs font-mono text-[#1D4ED8] hover:underline mt-2 inline-block">
                Go to Resumes Hub to upload one →
              </Link>
            </div>
          ) : (
            <div className="space-y-2 mb-3">
              {savedResumes.map((r) => {
                const isActive = selectedResumeId === r.resume_id;
                const isActiveOriginal = isActive && selectedResumeVariant === "original";
                const isActiveATS = isActive && selectedResumeVariant === "optimized";
                const isLoadingOriginal = loadingResume && isActive && selectedResumeVariant === "original";
                const isLoadingATS = loadingResume && isActive && selectedResumeVariant === "optimized";
                return (
                  <div key={r.resume_id} className={`rounded-xl border p-3 bg-white transition-colors ${isActive ? "border-[#10B981]" : "border-border"}`}>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="font-semibold text-sm">{r.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{new Date(r.created_at).toLocaleDateString()}</div>
                      </div>
                      {isActive && (
                        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 flex-shrink-0">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleSelectResume(r.resume_id, "original", r.name)}
                        disabled={loadingResume}
                        className={`px-3 py-1.5 rounded-lg text-xs font-mono border transition-colors flex items-center gap-1.5 ${
                          isActiveOriginal
                            ? "bg-[#10B981] text-white border-[#10B981]"
                            : "border-border text-muted-foreground hover:border-[#10B981] hover:text-[#047857]"
                        }`}>
                        {isLoadingOriginal
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : isActiveOriginal
                            ? <Check className="w-3 h-3" />
                            : <Circle className="w-3 h-3" />}
                        Use original
                      </button>
                      {r.optimized_at && (
                        <button
                          onClick={() => handleSelectResume(r.resume_id, "optimized", (r.optimized_name || r.name) + " (ATS)")}
                          disabled={loadingResume}
                          className={`px-3 py-1.5 rounded-lg text-xs font-mono border transition-colors flex items-center gap-1.5 ${
                            isActiveATS
                              ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                              : "border-border text-muted-foreground hover:border-[#7C3AED] hover:text-[#7C3AED]"
                          }`}>
                          {isLoadingATS
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : isActiveATS
                              ? <Check className="w-3 h-3" />
                              : <Circle className="w-3 h-3" />}
                          Use ATS version
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <Link to="/resumes" className="text-[10px] font-mono text-muted-foreground hover:text-[#1D4ED8] inline-block mt-1">
                Manage resumes in Resumes Hub →
              </Link>
            </div>
          )}

          {rbResume.trim().length > 0 && (
            <p className="text-xs text-[#10B981] mt-1.5 font-mono">✓ Resume ready — questions will be matched to your skills</p>
          )}
        </div>
      )}

      {selectedRole && selectedRounds.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 px-4 py-3 mb-6">
          <ShieldCheck className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-semibold text-[#10B981]">AI proctoring is active during this session.</span>{" "}
            Face visibility, eye direction, tab switching, window focus, and multiple persons in frame are monitored and logged to your integrity report.
          </p>
        </div>
      )}

      <div className="mt-6 flex justify-between items-center flex-wrap gap-4">
        <div className="font-mono text-xs text-muted-foreground">
          {selectedRole && selectedRounds.length > 0
            ? `${totalQuestions} question${totalQuestions !== 1 ? "s" : ""} · ${selectedRole.name} · ${selectedRounds.map((r) => `${ROUND_LABELS[r]} (${roundCounts[r] || 1})`).join(" + ")}`
            : "Complete setup above to begin"}
        </div>
        <Button
          onClick={startInterview}
          disabled={submitting || !canStart}
          loading={submitting}
          size="lg"
          data-testid="start-interview"
        >
          {submitting ? "Analysing resume & preparing questions…" : "Start interview"}
        </Button>
      </div>
      {startProgress.isRunning && (
        <ProgressSteps steps={START_INTERVIEW_STEPS} currentStep={startProgress.currentStep} pct={startProgress.pct} />
      )}
    </main>
    </>
  );
}
